
export interface UserProfile {
  id?: string;
  email?: string;
  name: string;
  partnerName: string;
  dueDate: string; // ISO string
  isOnboarded: boolean;
  role: 'partner' | 'pregnant';
  focusAreas: string[];
  inviteCode?: string;
  linkedPartnerId?: string;
  linkedPartnerName?: string;
  linkedPartnerEmail?: string;
  linkedPartnerRole?: 'partner' | 'pregnant';
  linkedPartnerDueDate?: string;
  linkedAt?: number; // Unix timestamp when partners connected
  isLinked?: boolean;
  comparisonTheme?: 'fruit' | 'sports' | 'geek' | 'tools' | 'snacks';
  emergencyContact?: string; // SOS phone number
  termsAcceptedAt?: string; // ISO timestamp when user accepted Terms of Service
  privacyAcceptedAt?: string; // ISO timestamp when user accepted Privacy Policy
  ageVerified?: boolean; // User confirmed they are 18+
}


export interface DailyCheckIn {
  date: string;
  myState: string;
  partnerSymptoms: string[];
  contextTags: string[];
}

export interface DailyGuidance {
  week: number;
  theme: string;
  context: string;
  actionItem: string;
  script: string;
  selfCare: string;
  babyDev: string;
  babySize: string; // e.g., "Size of a retro joystick"
}

export interface PollOption {
  id: string;
  text: string;
  votes: number;
}

export interface Poll {
  question: string;
  options: PollOption[];
  totalVotes: number;
  userVotedOptionId?: string;
}

export interface ChatMessage {
  id: string;
  sender: 'user' | 'ai' | 'peer';
  senderName?: string;
  text: string;
  poll?: Poll;
  timestamp: number;
  isLaborMode?: boolean;
}

export enum AppView {
  AUTH = 'AUTH',
  ONBOARDING = 'ONBOARDING',
  DASHBOARD = 'DASHBOARD',
  JOURNEY = 'JOURNEY',
  COMMUNITY = 'COMMUNITY',
  LABOR_MODE = 'LABOR_MODE',
  TOOLS = 'TOOLS',
  SETTINGS = 'SETTINGS',
  LEGAL = 'LEGAL',
  ADMIN = 'ADMIN'
}

export interface Contraction {
  id: string;
  startTime: number;
  endTime?: number;
  duration?: number; // seconds
}

export interface KickSession {
  id: string;
  startTime: number;
  endTime?: number;
  kicks: number[]; // timestamps of kicks
  duration: number; // seconds
}

export interface ChecklistItem {
  id: string;
  text: string;
  category: 'mom' | 'partner' | 'baby' | 'docs';
  isChecked: boolean;
  addedBy: 'user' | 'ai';
}

export interface MilestoneInfo {
  title: string;
  description: string;
  partnerTip: string;
  logistics: string;
  source?: string; // e.g., "ACOG", "March of Dimes"
  learnMoreUrl?: string; // Link to authoritative source
}

export interface FoodSafetyResult {
  item: string;
  status: 'Safe' | 'Caution' | 'Avoid';
  reason: string;
  alternatives?: string;
  source?: string; // e.g., "FDA", "ACOG"
  learnMoreUrl?: string; // Link to authoritative source
}

export interface NameIdea {
  name: string;
  gender: 'neutral' | 'girl' | 'boy';
  origin: string;
  meaning: string;
}

export interface Appointment {
  id: string;
  date: string;
  time: string;
  title: string; // e.g., "24 Week Checkup"
  notes: string;
  questions: QuestionItem[];
}

export interface QuestionItem {
  id: string;
  text: string;
  isAnswered: boolean;
  category: 'medical' | 'logistics' | 'baby';
  aiGenerated?: boolean;
}

export interface BumpPhoto {
  id: string;
  week: number;
  date: string;
  imageUrl: string; // Base64 or URL
  caption?: string;
}

export interface BirthPlanSection {
  id: string;
  title: string; // e.g., "Environment", "Pain Management"
  preferences: string[];
  partnerRole: string; // Specific instruction for the partner
}

export interface BirthPlan {
  sections: BirthPlanSection[];
  hospitalName?: string;
  providerName?: string;
}

export interface ScanResult {
  category: 'food' | 'product' | 'document' | 'unknown';
  safetyStatus: 'Safe' | 'Caution' | 'Avoid' | 'Info';
  title: string;
  summary: string;
  actionableAdvice: string;
}

export interface WeightEntry {
  id: string;
  date: string;
  weight: number;
  week: number;
}

export interface FlashFact {
  id: string;
  category: 'baby' | 'mom' | 'partner' | 'science';
  fact: string;
  emoji: string;
  source?: string; // e.g., "American Pregnancy Association"
  learnMoreUrl?: string; // Link to learn more
  week?: number; // Optional: specific week this fact is most relevant
}
